import { MouseEvent, useState } from "react";

// Interfaces
import { ISideTreeAction, ISideTreeActions } from "./interfaces";

// Components
import ButtonSelector from "@/Layouts/components/Buttons/SideSelectorButtons/ButtonSelector";

// MUI
import { Menu, MenuItem, Skeleton, Stack } from "@mui/material";

function ActionMenuButton(props: Readonly<ISideTreeAction>) {
	const {
		classes = "",
		color = "",
		menus = [],
		icon = null,
		iconHover = null,
		disabled = false,
		doubleCheckingAction = false,
		loading = false,
		onClick = () => {},
	} = props;

	const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
	const isMenuOpen = Boolean(anchorEl);

	const handleMenuOpen = (event?: MouseEvent<HTMLButtonElement>) => {
		if (event) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleMenuItemClick = (action = "", callback = () => {}) => {
		setAnchorEl(null);
		onClick?.(action);
		callback?.();
	};

	return (
		<Stack>
			<ButtonSelector
				classes={classes}
				color={color}
				confirmationButtonColor={color}
				icon={icon}
				iconHover={iconHover}
				isDoubleChecking={doubleCheckingAction}
				loading={loading}
				disabled={disabled}
				onActionClick={handleMenuOpen}
			/>

			<Menu
				open={isMenuOpen}
				anchorEl={anchorEl}
				className={"side-sel-opt-btn-menu"}
				MenuListProps={{
					"aria-labelledby": "side-sel-opt-btn",
				}}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				onClose={handleMenuClose}
			>
				{menus.map((menu, key) => {
					const {
						identifier = "",
						label = "",
						classes: className = "",
						disabled: disabledMenu = false,
						display = true,
						onClick: actionCallback = () => {},
					} = menu;
					const menuKey = `${label}_${key}`;

					if (!display) {
						return null;
					}

					return (
						<MenuItem
							key={menuKey}
							className={className}
							disabled={disabled || disabledMenu}
							onClick={() => handleMenuItemClick(identifier, actionCallback)}
						>
							{label}
						</MenuItem>
					);
				})}
			</Menu>
		</Stack>
	);
}

function ActionButton(props: Readonly<ISideTreeAction>) {
	const {
		classes = "",
		color = "",
		menus = [],
		icon = null,
		iconHover = null,
		disabled = false,
		doubleCheckingAction = false,
		loading = false,
		onClick = () => {},
	} = props;

	if (loading) {
		return (
			<Stack sx={{ padding: "10px" }}>
				<Skeleton variant="circular" width={30} height={30} />
			</Stack>
		);
	}

	if (menus?.length) {
		return <ActionMenuButton {...props} />;
	}

	return (
		<ButtonSelector
			classes={classes}
			color={color}
			confirmationButtonColor={color}
			icon={icon}
			iconHover={iconHover}
			isDoubleChecking={doubleCheckingAction}
			loading={loading}
			disabled={disabled}
			onActionClick={onClick}
		/>
	);
}

export default function SideTreeActions(props: Readonly<ISideTreeActions>) {
	const { operations = [], classes = "px-[15px]", alignment = "center", spacing = 1, sx = null } = props;

	return (
		<Stack direction="row" justifyContent={alignment} className={classes} spacing={spacing} sx={sx}>
			{operations.map((operation: ISideTreeAction, key) => {
				const { identifier = "", display = true } = operation;
				const operationKey = `${identifier}_${key}`;
				if (!display) {
					return null;
				}
				return (
					<Stack key={operationKey} direction="row">
						<ActionButton {...operation} />
					</Stack>
				);
			})}
		</Stack>
	);
}
